import { createSlice } from "@reduxjs/toolkit";

// ════════════════════════════════════════════════════════════════════════
//  Company & Store filter — shared between the SIDEBAR filter and every page
//  that has its own Company/Store filter.
//
//  Selection shape (same as CompanyStoreSelector's onChange):
//    { companyIds, storeIds }   null = all, [] = none, [ids] = exactly these
//
//    global  what was last picked in the SIDEBAR
//    pages   per-page selection ONLY for pages where the user changed the
//            filter inside the page itself: { [pageKey]: { companyIds, storeIds } }
//
//  A page uses   pages[pageKey] ?? global.
//    • Picking in the sidebar sets `global` AND clears every page override,
//      so every page now shows the sidebar's selection.
//    • Picking inside a page only writes that page's override, so when the
//      user comes back to that page it still shows its own last selection
//      (not the sidebar's) — until the sidebar is changed again.
//  `rev` counts sidebar changes so an OPEN page knows to reload its data.
//  Persisted in localStorage, reset on login / logout.
// ════════════════════════════════════════════════════════════════════════

const KEY = "ms_scope_filter";
const ALL = { companyIds: null, storeIds: null };

const load = () => {
  try {
    const s = JSON.parse(localStorage.getItem(KEY));
    if (s && s.global && s.pages) return s;
  } catch (_) { /* no saved selection */ }
  return null;
};
const save = (state) => {
  try { localStorage.setItem(KEY, JSON.stringify({ global: state.global, pages: state.pages })); } catch (_) { /* storage unavailable */ }
};

const saved = load();
const initialState = {
  global: saved?.global || ALL,
  pages: saved?.pages || {},
  rev: 0,
};

const resetAll = (state) => {
  state.global = ALL;
  state.pages = {};
  state.rev += 1;
  save(state);
};

const scopeFilterSlice = createSlice({
  name: "scopeFilter",
  initialState,
  reducers: {
    // Sidebar pick — applies to every page.
    setSidebarScope: (state, action) => {
      const { companyIds = null, storeIds = null } = action.payload || {};
      state.global = { companyIds, storeIds };
      state.pages = {};
      state.rev += 1;
      save(state);
    },
    // In-page pick — remembered for that page only.
    setPageScopeField: (state, action) => {
      const { page, field, value } = action.payload;
      const base = state.pages[page] || state.global;
      state.pages[page] = { ...base, [field]: value };
      save(state);
    },
    // A page's own narrowing (e.g. the one store a drill-down opened) is dropped → the page is back on the sidebar's selection.
    clearPageScope: (state, action) => {
      if (state.pages[action.payload]) { delete state.pages[action.payload]; save(state); }
    },
    resetScope: resetAll,
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (a) => a.type === "auth/login" || a.type === "auth/logout",
      resetAll
    );
  },
});

export const { setSidebarScope, setPageScopeField, clearPageScope, resetScope } = scopeFilterSlice.actions;
export default scopeFilterSlice.reducer;
